import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Typography, TextField, Button, Box, Avatar, IconButton,
  InputAdornment, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent,
  CircularProgress, Alert, Stack, Skeleton
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircleOutline, Link as LinkIcon } from '@mui/icons-material';
import { useCompanyCore } from '../../../../core/modules/company';
import { useGlobalCore } from '../../../../core/modules/global';
import { Integration } from '../../../../core/interfaces/integration.interface';
import { companiesApiService } from '../../../../core/modules/company/services/api';

const IntegrationTab = () => {
  // --- Estados del Componente ---
  const [availableIntegrations, setAvailableIntegrations] = useState<Integration[]>([]);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('');
  const [apiKey, setApiKey] = useState('');
  
  // Estados para la UI
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  // Hooks personalizados
  const { company, getCompany } = useCompanyCore();
  const { showSnackbar } = useGlobalCore();
  
  // Determinar si los datos actuales están guardados
  const isPristine = company?.integration?.id.toString() === selectedIntegrationId && company?.apiKey === apiKey;

  // --- Lógica de Carga de Datos ---
  const fetchData = useCallback(async () => {
    if (!company?.id) return;

    setIsLoading(true);
    setApiError(null);
    try {
      // Obtener datos en paralelo
      const [integrationsResponse, companyResponse] = await Promise.all([
        companiesApiService.getIntegrations(),
        companiesApiService.getCompany(company.id),
      ]);
      
      setAvailableIntegrations(integrationsResponse);

      // Si la compañía ya tiene una integración guardada, pre-seleccionarla
      if (companyResponse.integration) {
        setSelectedIntegrationId(companyResponse.integration.id.toString());
        setApiKey(companyResponse.api_key || '');
      } else if (integrationsResponse.length > 0) {
        // Si no, seleccionar la primera de la lista por defecto
        setSelectedIntegrationId(integrationsResponse[0].id.toString());
      }
    } catch (error) {
      setApiError('No se pudieron cargar los datos de integración.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [company?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Manejadores de Eventos ---
  const handleIntegrationChange = (event: SelectChangeEvent<string>) => {
    const newId = event.target.value;
    setSelectedIntegrationId(newId);
    setApiKey(''); // Limpiar la API key al cambiar de integración
  };

  const handleSave = async () => {
    if (!company?.id) return;

    setIsSaving(true);
    setApiError(null);
    
    try {
      await companiesApiService.updateCompanyIntegration(company.id, {
        integrationId: selectedIntegrationId ? parseInt(selectedIntegrationId, 10) : null,
        apiKey: apiKey,
      });
      await getCompany(company.id);

      showSnackbar({ message: '¡Conexión guardada con éxito!', options: { variant: 'success' } });
    } catch (error: any) {
      const message = error.response?.data?.detail || 'La API Key es inválida o hubo un error.';
      setApiError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const selectedErp = availableIntegrations.find(erp => erp.id.toString() === selectedIntegrationId);

  // --- Renderizado ---
  if (isLoading) {
    return (
      <Box className="flex items-center justify-center p-8">
        <Card className="w-full max-w-md"><CardContent><Skeleton variant="rectangular" height={280} /></CardContent></Card>
      </Box>
    );
  }

  return (
    <Box className="flex items-center justify-center p-4 sm:p-8">
      <Card className="w-full max-w-md shadow-xl">
        <CardContent className="p-6 sm:p-8">
          <Stack spacing={3}>
            <Box className="text-center">
              <Typography variant="h5" component="h2" className="font-bold">Conectar con tu ERP</Typography>
              <Typography variant="body2" color="text.secondary">Sincroniza tus datos eligiendo tu plataforma.</Typography>
            </Box>

            <FormControl fullWidth>
              <InputLabel id="erp-select-label">Plataforma ERP</InputLabel>
              <Select
                labelId="erp-select-label"
                value={selectedIntegrationId}
                label="Plataforma ERP"
                onChange={handleIntegrationChange}
                renderValue={(selected) => {
                  const erp = availableIntegrations.find(opt => opt.id.toString() === selected);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={erp?.logo_url || ''} sx={{ width: 24, height: 24 }}>{erp?.name?.charAt(0)}</Avatar>
                      {erp?.name}
                    </Box>
                  );
                }}
              >
                {availableIntegrations.map((option) => (
                  <MenuItem key={option.id} value={option.id.toString()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={option.logo_url || ''} sx={{ width: 28, height: 28 }}>{option.name.charAt(0)}</Avatar>
                      {option.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedErp && (
              <TextField
                fullWidth
                label={`API Key de ${selectedErp.name}`}
                variant="outlined"
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                helperText="Introduce tu API key para conectar tu cuenta."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowApiKey(!showApiKey)} edge="end">
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            {apiError && <Alert severity="error">{apiError}</Alert>}

            <Button
              fullWidth
              variant="contained"
              color={isPristine ? 'success' : 'primary'}
              size="large"
              onClick={handleSave}
              disabled={!apiKey || isSaving || isPristine}
              startIcon={
                isSaving ? <CircularProgress size={24} color="inherit" /> : 
                isPristine ? <CheckCircleOutline /> : <LinkIcon />
              }
            >
              {isSaving ? 'Validando...' : isPristine ? 'Conectado' : 'Guardar y Conectar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default IntegrationTab;